import { MapPin, Calendar, Users, CreditCard, Shield } from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import PaymentForm from "../../../components/booking/payment-form/PaymentForm";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import type { RootState } from "../../../store";
import { useCreateBookingMutation } from "../../../store/api/bookingApi";
import { useGetEventByIdQuery } from "../../../store/api/eventApi";
import { ROUTES } from "../../../utils/constants";
import { formatCurrency, formatDateTime } from "../../../utils/helpers";

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get("eventId");
  const { selectedSeats } = useSelector((state: RootState) => state.booking);
  const [createBooking, { isLoading: isBookingLoading }] = useCreateBookingMutation();
  const { data: eventData } = useGetEventByIdQuery(eventId!);

  const event = eventData?.data;
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "paypal">("stripe");
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (!eventId || selectedSeats.length === 0) {
      navigate(ROUTES.EVENTS);
      return;
    }

    // Calculate total amount based on selected seats
    if (event) {
      const amount = selectedSeats.reduce((total, seatId) => {
        // This would normally get the actual seat price from the event data
        const basePrice = event.ticketTypes[0]?.price || 0;
        return total + basePrice;
      }, 0);
      setTotalAmount(amount);
    }
  }, [eventId, selectedSeats, event, navigate]);

  const handlePaymentSuccess = async (paymentData: any) => {
    try {
      const bookingData = {
        eventId: eventId!,
        seats: selectedSeats,
        paymentMethod,
        ...paymentData,
      };

      const result = await createBooking(bookingData).unwrap();
      navigate(`${ROUTES.CONFIRMATION}?bookingId=${result.data?.id}`);
    } catch (error) {
      console.error("Booking failed:", error);
    }
  };

  if (!event) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Event Not Found</h1>
          <Button onClick={() => navigate(ROUTES.EVENTS)}>Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mx-auto max-w-4xl'>
        <h1 className='mb-8 text-3xl font-bold'>Complete Your Booking</h1>

        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          {/* Booking Summary */}
          <div className='space-y-6 lg:col-span-2'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Calendar className='h-5 w-5' />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <h3 className='text-lg font-semibold'>{event.title}</h3>
                  <p className='text-muted-foreground'>{event.description}</p>
                </div>

                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <div className='flex items-center gap-2'>
                    <Calendar className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>{formatDateTime(event.datetime)}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MapPin className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>{event.ground?.location.city}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Users className='h-5 w-5' />
                  Selected Seats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-4 gap-2 md:grid-cols-6'>
                  {selectedSeats.map(seatId => (
                    <Badge key={seatId} variant='secondary' className='justify-center'>
                      {seatId.split("-").slice(-2).join("")}
                    </Badge>
                  ))}
                </div>
                <div className='mt-4 text-sm text-muted-foreground'>
                  {selectedSeats.length} seat{selectedSeats.length !== 1 ? "s" : ""} selected
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <CreditCard className='h-5 w-5' />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentForm
                  amount={totalAmount}
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                  onPaymentSuccess={handlePaymentSuccess}
                  isLoading={isBookingLoading}
                />
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span>Seats ({selectedSeats.length})</span>
                    <span>{formatCurrency(totalAmount)}</span>
                  </div>
                  <div className='flex justify-between text-sm'>
                    <span>Service Fee</span>
                    <span>{formatCurrency(totalAmount * 0.05)}</span>
                  </div>
                  <div className='flex justify-between text-sm'>
                    <span>Processing Fee</span>
                    <span>{formatCurrency(2.99)}</span>
                  </div>
                </div>

                <Separator />

                <div className='flex justify-between font-semibold'>
                  <span>Total</span>
                  <span>{formatCurrency(totalAmount + totalAmount * 0.05 + 2.99)}</span>
                </div>

                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Shield className='h-3 w-3' />
                  <span>Secure payment processing</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Booking Policy</CardTitle>
              </CardHeader>
              <CardContent className='space-y-2 text-sm text-muted-foreground'>
                <p>• Tickets are non-refundable</p>
                <p>• Event may be cancelled due to weather</p>
                <p>• No outside food or drinks allowed</p>
                <p>• Gates open 1 hour before event</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
